import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, CheckCircle2, XCircle, Inbox, AlertTriangle, ArrowDown, ArrowUp, Eye, Camera, Calculator, X, CheckCheck, Search, Minus, Pencil } from 'lucide-react';import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { api, fileURL, photoURL } from '../../lib/api';
import { ZoomableImage } from '../../components/ZoomableImage';
import { useWindows } from '../../context/WindowsContext';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const FUEL_ORDER = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const FUEL_LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const SVC_LABEL = { self: 'Auto', full: 'Full' };
// Markers SIN captura en la ronda (faltantes/stale) no se muestran en el preview
// para no distorsionar con fotos/precios de días anteriores.
const _missSet = (req) => new Set((req?.missing_markers || []).map(n => String(n).trim().toLowerCase()));
const freshComps = (req) => {
  const miss = _missSet(req);
  return (req?.competitor_snapshot || []).filter(c => !c.stale && !miss.has(String(c.name || '').trim().toLowerCase()));
};
const freshDiffs = (req) => {
  const miss = _missSet(req);
  return (req?.consolidated_diffs || []).filter(d => !miss.has(String(d.marker_name || '').trim().toLowerCase()));
};

const tabs = [
  { key: 'open', label: 'Pendientes' },
  { key: 'reviewed', label: 'Revisadas' },
  { key: 'approved', label: 'Aprobadas' },
  { key: 'rejected', label: 'Rechazadas' },
];

const ORIGIN_LABELS = { masivo: 'Push masivo', tracking: 'Pushito (tracking)', monitor: 'Pushito (monitor)' };
const fmtShort = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const PEND_FUEL = { vpower: 'V-Pwr', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const pendSummary = (fp) => Object.entries(fp || {})
  .map(([f, v]) => { const n = Number(v && typeof v === 'object' ? v.self : v); return Number.isFinite(n) && n > 0 ? `${PEND_FUEL[f] || f} Q${n.toFixed(2)}` : null; })
  .filter(Boolean).join(' · ');

const PriceCell = ({ value, ourValue, prev }) => {
  if (value == null) return <span className="text-[#a8a397]">—</span>;
  const num = Number(value);
  let cls = 'bg-[#f3f0e9] text-[#3a3a3a]';
  if (num !== 0 && ourValue != null && Number(ourValue) > 0) {
    const d = num - Number(ourValue);
    if (d < -0.001) cls = 'bg-[#fee2e2] text-[#991b1b]';
    else if (d > 0.001) cls = 'bg-[#dcfce7] text-[#166534]';
  }
  const pv = prev != null ? Number(prev) : null; // eslint-disable-line no-unused-vars
  return (
    <span className="inline-flex flex-col items-end">
      <span className={`inline-block min-w-[58px] text-right font-mono tabular-nums px-2 py-0.5 rounded text-[12px] ${cls}`}>Q{num.toFixed(2)}</span>
    </span>
  );
};

// Build suggested prices from a competitor snapshot + global parameters.
// Súper = min(competitor.super); Diesel = min(competitor.diesel)
// V-Power = Súper + diff_vp_super | Regular = Súper - diff_super_regular
// ULSD = Diesel + diff_diesel_ulsd
const ENDING_BY_BRAND = { uno: 7, shell: 9 };

// Ajusta el precio hacia abajo hasta que los centavos terminen en el dígito de la marca (UNO→7, Shell→9).
const applyBrandEnding = (value, brand) => {
  const digit = ENDING_BY_BRAND[(brand || '').trim().toLowerCase()];
  if (digit == null || !Number.isFinite(value) || value <= 0) return +value.toFixed(2);
  let cents = Math.round(value * 100);
  while (cents % 10 !== digit) cents -= 1;
  return +(cents / 100).toFixed(2);
};

const buildSuggestedPrices = (comps, params, ownBrand) => {
  const svcKeys = ['self', 'full'];
  const result = {};
  const minOf = (fuel, svc) => {
    const vals = comps
      .map(c => Number(c.prices?.[fuel]?.[svc]))
      .filter(v => Number.isFinite(v) && v > 0);
    return vals.length ? Math.min(...vals) : null;
  };
  const fin = (v) => applyBrandEnding(v, ownBrand);
  svcKeys.forEach(svc => {
    const superBase = minOf('super', svc);
    const dieselBase = minOf('diesel', svc);
    if (superBase != null) {
      const vp = superBase + Number(params.diff_vp_super || 0);
      const reg = superBase - Number(params.diff_super_regular || 0);
      result.super = result.super || {}; result.super[svc] = fin(superBase);
      result.vpower = result.vpower || {}; result.vpower[svc] = fin(vp);
      result.regular = result.regular || {}; result.regular[svc] = fin(reg);
    }
    if (dieselBase != null) {
      const ulsd = dieselBase + Number(params.diff_diesel_ulsd || 0);
      result.diesel = result.diesel || {}; result.diesel[svc] = fin(dieselBase);
      result.ulsd = result.ulsd || {}; result.ulsd[svc] = fin(ulsd);
    }
  });
  return result;
};

// Nested viewer for a competitor's latest approved capture photo.
const CompetitorPhotoDialog = ({ station, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !station) return;
    if (station.push_photo) {
      setData({ photo_url: station.push_photo.photo_url, captured_at: station.push_photo.confirmed_at,
                captured_by_name: station.push_photo.confirmed_by_name, photo_type: 'monolito' });
      setLoading(false);
      return;
    }
    setLoading(true); setData(null);
    const params = {};
    if (station.entry_id) params.entry_id = station.entry_id;
    if (station.svc) params.service_type = station.svc;
    api.get(`/stations/${station.id}/latest-photo`, { params })
      .then(r => setData(r.data))
      .catch(() => setData({ photo_url: null }))
      .finally(() => setLoading(false));
  }, [open, station]);
  if (!station) return null;
  const svcLabel = station.svc === 'full' ? 'Servicio completo' : station.svc === 'self' ? 'Autoservicio' : null;
  const subtitle = station.push_photo
    ? 'monolito · confirmación del último cambio (push/pushito)'
    : station.own
      ? 'foto más reciente (captura o monolito)'
      : station.entry_id
      ? `captura de esta ronda de tracking${svcLabel ? ' · ' + svcLabel : ''}`
      : svcLabel ? `última captura · ${svcLabel}` : 'última captura aprobada';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#f8f6f1] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-[20px] tracking-tight">{station.name}</DialogTitle>
          <p className="text-[11.5px] text-[#5a5a5a]">{station.brand} · {subtitle}</p>
        </DialogHeader>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
        ) : data?.photo_url ? (
          <div className="space-y-3">
            <ZoomableImage
              src={photoURL(data.photo_url)}
              alt={station.name}
              maxH="70vh"
              testId="competitor-photo"
            />
            <p className="text-[11.5px] text-[#5a5a5a]">
              Capturada por <strong>{data.captured_by_name || '—'}</strong> · {data.captured_at ? new Date(data.captured_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—'} · {data.photo_type === 'monolito' ? 'Monolito' : (data.service_type === 'full' ? 'Servicio completo' : 'Autoservicio')}
            </p>
          </div>
        ) : (
          <div className="py-12 text-center">
            <Camera size={28} strokeWidth={1.4} className="mx-auto text-[#a8a397] mb-2"/>
            <p className="text-[12.5px] text-[#8a8579]">No hay foto aprobada disponible para esta estación.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PricePreviewModal = ({ open, onOpenChange, request, onReview, busy, onMinimize, onCorrected }) => {  const [params, setParams] = useState(null);
  const { user: _authUser } = useAuth();
  const _isAdmin = _authUser?.role === 'admin';
  const [photoStation, setPhotoStation] = useState(null);
  const [mode, setMode] = useState('view'); // 'view' | 'approving'
  const [approved, setApproved] = useState({});
  const [included, setIncluded] = useState({});
  const [stationPush, setStationPush] = useState(null);
  const [bus, setBus] = useState({});
  const [pricingCfg, setPricingCfg] = useState(null);
  const [rsopRef, setRsopRef] = useState(null);
  const [notes, setNotes] = useState('');
  const [fixing, setFixing] = useState(false);
  const [fixPrices, setFixPrices] = useState({});
  const [savingFix, setSavingFix] = useState(false);
  const [fixedOverride, setFixedOverride] = useState(null);
  useEffect(() => { setNotes(''); setFixing(false); setFixedOverride(null); }, [request?.id]);

  const loadRules = () => {
    api.get('/parameters').then(r => setParams(r.data)).catch(() => setParams(null));
    if (request?.station_id) {
      api.get(`/stations/${request.station_id}/pricing-config`).then(r => setPricingCfg(r.data)).catch(() => setPricingCfg(null));
      api.get(`/stations/${request.station_id}/rsop-reference`).then(r => setRsopRef(r.data)).catch(() => setRsopRef(null));
    }
  };

  useEffect(() => {
    if (!open) return;
    loadRules();
    setMode('view');
    setApproved({});
    setStationPush(null);
    // BU de la estación propia y de los markers
    const ids = [request?.station_id, ...((request?.competitor_snapshot || []).map(c => c.id))].filter(Boolean);
    if (ids.length) {
      api.get('/stations-bus', { params: { ids: ids.join(',') } }).then(r => setBus(r.data || {})).catch(() => setBus({}));
    }
    // Fetch station_push details (OCR result) if request has been approved and has auto_station_push_id
    if (request?.auto_station_push_id) {
      api.get(`/station-pushes/${request.auto_station_push_id}`)
        .then(r => setStationPush(r.data))
        .catch(() => { /* not available yet */ });
    }
  }, [open, request?.id, request?.auto_station_push_id]);

  // Prefill (o re-prefill) de sugeridos cuando las reglas terminan de cargar
  const rulesReady = !!params && !!pricingCfg;
  // Solo los productos CON movimiento van marcados por default (sugerido ≠ vigente)
  const movedVs = (suggested) => {
    const ourP = request?.our_prices || {};
    const inc = {};
    const diff = (a, b) => a != null && (b == null || Math.abs(Number(a) - Number(b)) >= 0.005);
    Object.entries(suggested).forEach(([f, slot]) => {
      const o = ourP[f] || {};
      inc[f] = diff(slot?.self, o.self) || diff(slot?.full, o.full);
    });
    return inc;
  };

  // Solo los combustibles VISIBLES en la matriz pueden aprobarse/enviarse
  const visibleFuels = () => {
    const set = new Set(Object.keys(request?.our_prices || {}));
    (request?.competitor_snapshot || []).forEach(c => Object.keys(c.prices || {}).forEach(k => set.add(k)));
    const avail = pricingCfg?.available_products?.length ? pricingCfg.available_products : null;
    return FUEL_ORDER.filter(k => set.has(k) && (!avail || avail.includes(k)));
  };

  // Full SIEMPRE se recalcula desde el Auto sugerido con el Dif. Auto/Full de la
  // estación (el min de competencia en full suele traer el precio viejo).
  const applyFullDiff = (suggested) => {
    if (!pricingCfg) return suggested;
    if (pricingCfg?.services?.full === false) {
      Object.values(suggested).forEach(slot => { if (slot) delete slot.full; });
      return suggested;
    }
    const afd = (f) => Number(pricingCfg?.product_adjustments?.[f]?.auto_full_diff ?? 1.1);
    Object.entries(suggested).forEach(([f, slot]) => {
      const s = Number(slot?.self);
      if (Number.isFinite(s) && s > 0) slot.full = +(s + afd(f)).toFixed(2);
    });
    return suggested;
  };

  useEffect(() => {
    if (!request || mode !== 'approving' || !rulesReady) return;
    const vis = visibleFuels();
    const suggested = applyFullDiff(buildSuggestedPrices(freshComps(request), params, request.station_brand));
    Object.keys(suggested).forEach(f => { if (!vis.includes(f)) delete suggested[f]; });
    setApproved(suggested);
    setIncluded(movedVs(suggested));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rulesReady]);

  if (!request) return null;
  const our = request.our_prices || {};
  const comps = freshComps(request);
  const vDiffs = freshDiffs(request);
  const anyFull = comps.some(c => Object.values(c.prices || {}).some(p => (p?.full ?? 0) !== 0))
    || Object.values(our).some(p => (p?.full ?? null) != null);
  const allFuelsSet = new Set();
  Object.keys(our).forEach(k => allFuelsSet.add(k));
  comps.forEach(c => Object.keys(c.prices || {}).forEach(k => allFuelsSet.add(k)));
  const ownAvail = pricingCfg?.available_products?.length ? pricingCfg.available_products : null;
  const fuels = FUEL_ORDER.filter(k => allFuelsSet.has(k) && (!ownAvail || ownAvail.includes(k)));
  const photoBtnCls = "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] bg-[#f3f0e9] hover:bg-[#e8e5de] text-[#3a3a3a] whitespace-nowrap";

  // Precio vigente mostrado en la fila propia (push > snapshot), con override tras corrección
  const currentOwn = (f) => {
    const o = our[f] || {};
    const l = request.last_push?.final_prices?.[f] || {};
    const base = { self: l.self != null ? l.self : o.self, full: l.full != null ? l.full : o.full };
    return fixedOverride?.[f] ? { ...base, ...fixedOverride[f] } : base;
  };

  const startFix = () => {
    setFixPrices(Object.fromEntries(fuels.map(f => {
      const c = currentOwn(f);
      return [f, { self: c.self ?? '', full: c.full ?? '' }];
    })));
    setFixing(true);
  };

  const saveFix = async () => {
    const payload = {};
    fuels.forEach(f => {
      const cur = currentOwn(f);
      const fp = fixPrices[f] || {};
      const slot = {};
      if (fp.self !== '' && fp.self != null && Number(fp.self) !== Number(cur.self)) slot.self = Number(fp.self);
      if (anyFull && fp.full !== '' && fp.full != null && Number(fp.full) !== Number(cur.full)) slot.full = Number(fp.full);
      if (Object.keys(slot).length) payload[f] = slot;
    });
    if (!Object.keys(payload).length) { toast.info('No hay cambios que corregir'); setFixing(false); return; }
    setSavingFix(true);
    try {
      const r = await api.put(`/stations/${request.station_id}/official-prices`, {
        prices: payload, note: `Corrección desde preview de tracking ${request.id}`,
      });
      setFixedOverride(prev => {
        const next = { ...(prev || {}) };
        Object.entries(r.data.corrected || payload).forEach(([f, s]) => { next[f] = { ...(next[f] || {}), ...s }; });
        return next;
      });
      setFixing(false);
      toast.success('Precio oficial corregido · sin pushes ni notificaciones');
      if (onCorrected) onCorrected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al corregir el precio oficial'); }
    finally { setSavingFix(false); }
  };

  const startApprove = () => {
    if (!rulesReady) loadRules();
    const vis = visibleFuels();
    const suggested = applyFullDiff(buildSuggestedPrices(comps, params || {}, request.station_brand));
    Object.keys(suggested).forEach(f => { if (!vis.includes(f)) delete suggested[f]; });
    setApproved(suggested);
    setIncluded(movedVs(suggested));
    setMode('approving');
  };

  const submitApprove = async () => {
    const vis = visibleFuels();
    const out = Object.fromEntries(Object.entries(approved).filter(([f]) => included[f] && vis.includes(f)));
    if (Object.keys(out).length === 0) {
      toast.error('Marca al menos un producto con movimiento (✓) para enviar el cambio');
      return;
    }
    await onReview(request.id, 'approve', { approved_prices: out, notes: notes.trim() });
    onOpenChange(false);
  };

  const submitReject = async () => {
    await onReview(request.id, 'reject', { notes: notes.trim() });
    onOpenChange(false);
  };

  // Al escribir cualquier Auto se recalcula su Full (Auto + Dif. Auto/Full de la
  // estación, AD-P06). Además: Regular → Súper y V-Power (diferenciales AD-P12);
  // Diesel → ULSD. Solo deriva con las reglas ya cargadas (rulesReady).
  const editApproved = (fuel, svc, val) => {
    setIncluded(p => (p[fuel] ? p : { ...p, [fuel]: true }));
    setApproved(prev => {
      const next = { ...prev, [fuel]: { ...(prev[fuel] || {}), [svc]: val === '' ? null : Number(val) } };
      if (svc !== 'self' || !rulesReady) return next;
      const base = Number(val);
      if (!Number.isFinite(base) || base <= 0) return next;
      const afd = (f) => Number(pricingCfg?.product_adjustments?.[f]?.auto_full_diff ?? 1.1);
      const fillFull = anyFull;
      const r2 = (v) => +v.toFixed(2);
      const put = (f, s, v) => {
        if (!fuels.includes(f)) return;
        if (ownAvail && !ownAvail.includes(f)) return;
        next[f] = { ...(next[f] || {}), [s]: r2(v) };
      };
      if (fillFull) put(fuel, 'full', base + afd(fuel));
      if (fuel === 'regular') {
        const sup = base + Number(params.diff_super_regular || 0);
        put('super', 'self', sup);
        if (fillFull) put('super', 'full', sup + afd('super'));
        const vp = sup + Number(params.diff_vp_super || 0);
        put('vpower', 'self', vp);
        if (fillFull) put('vpower', 'full', vp + afd('vpower'));
      } else if (fuel === 'diesel') {
        const ul = base + Number(params.diff_diesel_ulsd || 0);
        put('ulsd', 'self', ul);
        if (fillFull) put('ulsd', 'full', ul + afd('ulsd'));
      }
      return next;
    });
  };

  const reviewedFooter = request.status !== 'open' && (
    <p className="text-[11.5px] text-[#8a8579]">
      {request.status === 'approved' ? 'Aprobada' : 'Rechazada'} por <strong>{request.reviewed_by_name || '—'}</strong>
      {request.reviewed_at ? ` · ${new Date(request.reviewed_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
    </p>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[min(96vw,1200px)] bg-[#f8f6f1] max-h-[88vh] overflow-y-auto p-4 sm:p-6">
          {onMinimize && (
            <button
              type="button"
              onClick={() => onMinimize(request)}
              className="absolute right-12 top-4 w-7 h-7 rounded-full hover:bg-[#e8e5de] flex items-center justify-center transition-colors z-10"
              title="Minimizar · sigue navegando sin perder esta ventana"
              aria-label="Minimizar"
              data-testid="preview-minimize-btn"
            >
              <Minus size={15}/>
            </button>
          )}
          <DialogHeader>
            <DialogTitle className="font-serif text-[22px] tracking-tight flex items-center gap-2 flex-wrap">
              Preview de precios · {request.station_name}
              {request.source === 'auto_tracking' && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#e0e7ff] text-[#0f1d6f] tracking-wider uppercase" data-testid="auto-tracking-badge">Auto · tracking</span>
              )}
              {request.incomplete && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e] tracking-wider uppercase">Incompleto</span>
              )}
              {request.rsop_compare && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${request.rsop_compare.status === 'match' ? 'bg-[#dcfce7] text-[#166534]' : 'bg-[#fef3c7] text-[#92400e]'}`}
                  data-testid="preview-rsop-compare"
                  title={request.rsop_compare.status === 'match'
                    ? 'El precio vigente está en su RSOP (sugerido con flete/ajustes).'
                    : `Vigente vs RSOP: ${Object.entries(request.rsop_compare.diffs || {}).map(([f, d]) => `${FUEL_LABELS[f] || f} ${d > 0 ? '+' : ''}${d.toFixed(2)}`).join(' · ')}`}
                >
                  {request.rsop_compare.status === 'match' ? 'En sugerido' : 'Distinto al sugerido'}
                </span>
              )}
            </DialogTitle>
            <p className="text-[12px] text-[#5a5a5a]">
              {request.requested_by_name} · {request.requested_by_role || 'usuario'} · {new Date(request.requested_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}
              {bus[request.station_id]?.tm && <span className="ml-1.5 px-2 py-0.5 rounded-full bg-[#e8e5de] text-[#1c1c1c] text-[11px] font-medium" data-testid="preview-tm-name">TM: {bus[request.station_id].tm}</span>}
            </p>
            {request.incomplete && (request.missing_markers || []).length > 0 && (
              <p className="text-[11px] text-[#92400e]">Faltaron por capturar: {request.missing_markers.join(', ')}</p>
            )}
            {vDiffs.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="consolidated-diffs">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#e7eeff] text-[#0f1d6f]">Comparación en Autoservicio</span>
                {vDiffs.slice(0, 10).map((d, i) => (
                  <span key={i} className={`text-[10.5px] px-2 py-0.5 rounded-full font-mono ${d.diff > 0 ? 'bg-[#dcfce7] text-[#166534]' : 'bg-[#fee2e2] text-[#991b1b]'}`}>
                    {d.marker_name} · {String(d.fuel).toUpperCase()} {d.diff > 0 ? '+' : ''}{d.diff.toFixed(2)}
                  </span>
                ))}
                {vDiffs.length > 10 && (
                  <span className="text-[10.5px] text-[#8a8579]">+{vDiffs.length - 10} más</span>
                )}
              </div>
            )}
            {rsopRef?.push && Object.keys(rsopRef.rsop || {}).length > 0 ? (
              <div className="text-[10.5px] text-[#3a3a3a] bg-[#f3f0e9] border border-[#e8e5de] rounded-lg px-3 py-1.5 mt-1 font-normal text-left space-y-0.5" data-testid="rsop-reference">
                <div>
                  <strong>RSOP referencial · {request.station_name}:</strong>{' '}
                  Push {fmtShort(rsopRef.push.sent_at)}{rsopRef.push.created_by_name ? ` · ${rsopRef.push.created_by_name}` : ''}:{' '}
                  {FUEL_ORDER.filter(f => rsopRef.rsop[f]).map(f => `${FUEL_LABELS[f] || f} Q${rsopRef.rsop[f].base.toFixed(2)}${rsopRef.rsop[f].push_at ? ` (push ${fmtShort(rsopRef.rsop[f].push_at)})` : ''}`).join(' · ')}
                </div>
                <div className="text-[#5a5a5a]">
                  + Depósito planta: gasolinas +Q{rsopRef.department.deposito_gasolinas.toFixed(2)} / diésel +Q{rsopRef.department.deposito_diesel.toFixed(2)}
                  {' '}· + Flete{rsopRef.department.name ? ` (${rsopRef.department.name})` : ''}: +Q{rsopRef.department.diferencial_flete.toFixed(2)}
                  {FUEL_ORDER.some(f => rsopRef.rsop[f]?.ajuste) && (
                    <> · Ajuste estación: {FUEL_ORDER.filter(f => rsopRef.rsop[f]?.ajuste).map(f => `${FUEL_LABELS[f] || f} ${rsopRef.rsop[f].ajuste > 0 ? '+' : ''}Q${rsopRef.rsop[f].ajuste.toFixed(2)}`).join(' · ')}</>
                  )}
                </div>
                <div>
                  <strong>= RSOP:</strong>{' '}
                  {FUEL_ORDER.filter(f => rsopRef.rsop[f]).map(f => `${FUEL_LABELS[f] || f} Q${rsopRef.rsop[f].self.toFixed(2)}`).join(' · ')}
                </div>
              </div>
            ) : rsopRef && (
              <div className="text-[10.5px] text-[#8a8579] bg-[#f3f0e9] border border-[#e8e5de] rounded-lg px-3 py-1.5 mt-1 font-normal text-left" data-testid="rsop-reference">
                <strong>RSOP referencial:</strong> sin push masivo previo con precios base.
              </div>
            )}
          </DialogHeader>

          {fuels.length === 0 ? (
            <p className="text-[12px] text-[#8a8579] py-6 text-center">Esta solicitud no incluyó snapshot de precios.</p>
          ) : (
            <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-[#f8f6f1]">
                    <tr className="text-left text-[10.5px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                      <th className="py-2.5 px-3 font-normal sticky left-0 bg-[#f8f6f1] z-10 min-w-[110px]">Estación</th>
                      {fuels.map(f => (
                        <th key={f} colSpan={anyFull ? 2 : 1} className="py-2.5 px-3 font-normal text-center border-l border-[#e8e5de]">{FUEL_LABELS[f]}</th>
                      ))}
                      <th className="py-2.5 px-3 font-normal text-right">Foto</th>
                    </tr>
                    <tr className="text-left text-[10px] tracking-wider uppercase text-[#a8a397] border-b border-[#e8e5de]">
                      <th className="py-1.5 px-3 font-normal sticky left-0 bg-white z-10"></th>
                      {fuels.map(f => (
                        <React.Fragment key={f}>
                          <th className="py-1.5 px-3 font-normal text-right border-l border-[#e8e5de]">Auto</th>
                          {anyFull && <th className="py-1.5 px-3 font-normal text-right">Full</th>}
                        </React.Fragment>
                      ))}
                      <th className="py-1.5 px-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e8e5de]">
                    <tr className="bg-[#fffbeb]">
                      <td className="py-2 px-3 font-medium sticky left-0 bg-[#fffbeb] z-10 min-w-[110px]" data-testid="preview-row-own">
                        {request.station_name}
                        <div className="text-[10px] text-[#8a8579] font-normal">UNO · propio · precios vigentes{bus[request.station_id]?.bu ? ` · BU ${bus[request.station_id].bu}` : ''}</div>
                        {request.last_push ? (
                          <>
                            <div className="text-[10px] text-[#1e40af] font-normal mt-0.5" data-testid="own-last-push-info">
                              {ORIGIN_LABELS[request.last_push.origin] || request.last_push.origin} · {fmtShort(request.last_push.effective_at)}
                              {request.last_push.created_by_name ? ` · ${request.last_push.created_by_name}` : ''}
                            </div>
                            <div className="text-[9.5px] font-normal mt-0.5" data-testid="own-last-push-confirm">
                              {request.last_push.confirmed_at
                                ? <span className="text-[#166534]">✓ Confirmado con monolito</span>
                                : <span className="text-[#92400e]">Sin confirmar con monolito</span>}
                            </div>
                          </>
                        ) : (
                          <div className="text-[9.5px] text-[#8a8579] font-normal mt-0.5">Sin push previo · precios oficiales</div>
                        )}
                        {(request.pending_pushes || [])
                          .filter(p => p.station_push_id !== request.last_push?.station_push_id)
                          .map(p => (
                            <div key={p.station_push_id} className="text-[9.5px] font-normal mt-0.5 text-[#92400e] bg-[#fef3c7] rounded px-1.5 py-0.5" data-testid="own-pending-push">
                              También pendiente: {ORIGIN_LABELS[p.origin] || p.origin} · efectivo {fmtShort(p.effective_at)}
                              {p.created_by_name ? ` · ${p.created_by_name}` : ''}
                              {pendSummary(p.final_prices) ? ` — ${pendSummary(p.final_prices)}` : ''}
                            </div>
                          ))}
                        {!fixing ? (
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            {_isAdmin && (
                            <button
                              type="button"
                              onClick={startFix}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] bg-[#e8e5de] hover:bg-[#dcd8cd] text-[#3a3a3a] font-normal"
                              title="Corregir el precio oficial vigente sin generar pushes ni notificaciones"
                              data-testid="fix-official-btn"
                            >
                              <Pencil size={9}/> Corregir oficial
                            </button>
                            )}
                            {fixedOverride && <span className="text-[9px] text-[#166534] font-normal" data-testid="fix-official-done">✓ Corregido</span>}
                          </div>
                        ) : (
                          <div className="mt-1.5 font-normal">
                            <div className="flex items-center gap-1.5">
                              <button type="button" onClick={saveFix} disabled={savingFix}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] bg-[#15803d] hover:bg-[#166534] text-white disabled:opacity-50"
                                data-testid="fix-official-save">
                                {savingFix ? <Loader2 size={9} className="animate-spin"/> : <CheckCircle2 size={9}/>} Guardar
                              </button>
                              <button type="button" onClick={() => setFixing(false)} disabled={savingFix}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] bg-[#f3f0e9] hover:bg-[#e8e5de] text-[#3a3a3a]"
                                data-testid="fix-official-cancel">
                                Cancelar
                              </button>
                            </div>
                            <div className="text-[9px] text-[#92400e] mt-1">Corrección silenciosa: no envía push ni notifica a nadie</div>
                          </div>
                        )}
                      </td>
                      {fuels.map(f => {
                        const p = currentOwn(f);
                        return (
                          <React.Fragment key={f}>
                            <td className="py-2 px-3 text-right border-l border-[#e8e5de]">
                              {fixing ? (
                                <Input type="number" step="0.01"
                                  value={fixPrices[f]?.self ?? ''}
                                  onChange={e => setFixPrices(prev => ({ ...prev, [f]: { ...(prev[f] || {}), self: e.target.value } }))}
                                  className="w-[84px] h-8 bg-white text-[12px] font-mono tabular-nums text-right border-[#e8a33d] border-2 ml-auto"
                                  data-testid={`fix-${f}-self`}/>
                              ) : p.self != null ? <span className="inline-block min-w-[58px] text-right font-mono tabular-nums px-2 py-0.5 rounded text-[12px] bg-white border border-[#e8e5de]">Q{Number(p.self).toFixed(2)}</span> : <span className="text-[#a8a397]">—</span>}
                            </td>
                            {anyFull && (
                              <td className="py-2 px-3 text-right">
                                {fixing ? (
                                  <Input type="number" step="0.01"
                                    value={fixPrices[f]?.full ?? ''}
                                    onChange={e => setFixPrices(prev => ({ ...prev, [f]: { ...(prev[f] || {}), full: e.target.value } }))}
                                    className="w-[84px] h-8 bg-white text-[12px] font-mono tabular-nums text-right border-[#e8a33d] border-2 ml-auto"
                                    data-testid={`fix-${f}-full`}/>
                                ) : p.full != null ? <span className="inline-block min-w-[58px] text-right font-mono tabular-nums px-2 py-0.5 rounded text-[12px] bg-white border border-[#e8e5de]">Q{Number(p.full).toFixed(2)}</span> : <span className="text-[#a8a397]">—</span>}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <td className="py-2 px-2 text-right">
                        <div className="flex flex-col items-end gap-1">
                          <button
                            type="button"
                            onClick={() => setPhotoStation(request.last_confirm_photo
                              ? { id: request.station_id, name: request.station_name, brand: 'UNO', push_photo: request.last_confirm_photo }
                              : { id: request.station_id, name: request.station_name, brand: 'UNO', own: true })}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] bg-[#fef3c7] hover:bg-[#fde68a] text-[#3a3a3a] whitespace-nowrap"
                            data-testid="photo-btn-own"
                            title={request.last_confirm_photo ? 'Foto del monolito · confirmación del último cambio' : 'Ver última foto del monolito'}
                          >
                            <Camera size={11}/> Monolito
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Suggested row when approving */}
                    {mode === 'approving' && (
                      <tr className="bg-[#dcfce7]/60">
                        <td className="py-2 px-3 font-medium sticky left-0 bg-[#e9f9ef] z-10 min-w-[110px]">
                          <span className="inline-flex items-center gap-1.5"><Calculator size={12}/> Precios sugeridos</span>
                          <div className="text-[10px] text-[#3a3a3a] font-normal">Editable antes de aprobar</div>
                          <div className="text-[9.5px] text-[#166534] font-normal mt-0.5">Escribe <b>Regular</b> y <b>Diesel</b> en Auto → el resto se calcula por reglas</div>
                          <div className="text-[9.5px] text-[#92400e] font-normal mt-0.5">Solo los productos con ✓ llegan a la estación · los demás conservan su último precio</div>
                        </td>
                        {fuels.map(f => {
                          if (ownAvail && !ownAvail.includes(f)) {
                            return (
                              <td key={f} colSpan={anyFull ? 2 : 1} className="py-1 px-2 text-center border-l border-[#e8e5de] text-[10.5px] text-[#8a8579]" data-testid={`approved-${f}-na`}>
                                No vende
                              </td>
                            );
                          }
                          return (
                          <React.Fragment key={f}>
                            <td className="py-1 px-2 text-right border-l border-[#e8e5de]">
                              <label className="flex items-center justify-end gap-1 mb-1 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={!!included[f]}
                                  onChange={e => setIncluded(p => ({ ...p, [f]: e.target.checked }))}
                                  className="h-3.5 w-3.5 accent-[#15803d]"
                                  data-testid={`approved-${f}-check`}
                                />
                                <span className={`text-[9.5px] ${included[f] ? 'text-[#166534] font-medium' : 'text-[#8a8579]'}`}>{included[f] ? 'Cambia' : 'Sin mov.'}</span>
                              </label>
                              <Input
                                type="number"
                                step="0.01"
                                value={approved[f]?.self ?? ''}
                                onChange={e => editApproved(f, 'self', e.target.value)}
                                disabled={!rulesReady || !included[f]}
                                className={`w-[92px] h-8 bg-white text-[12.5px] font-mono tabular-nums text-right disabled:opacity-40 ${(f === 'regular' || f === 'diesel') ? 'border-2 border-[#16a34a]' : 'border-[#bbf7d0]'}`}
                                data-testid={`approved-${f}-self`}
                              />
                            </td>
                            {anyFull && (
                              <td className="py-1 px-2 text-right align-bottom">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={approved[f]?.full ?? ''}
                                  onChange={e => editApproved(f, 'full', e.target.value)}
                                  disabled={!rulesReady || !included[f]}
                                  className="w-[92px] h-8 bg-white text-[12.5px] font-mono tabular-nums text-right border-[#bbf7d0] disabled:opacity-40"
                                  data-testid={`approved-${f}-full`}
                                />
                              </td>
                            )}
                          </React.Fragment>
                          );
                        })}
                        <td/>
                      </tr>
                    )}

                    {comps.map(c => (
                      <tr key={c.id} data-testid={`preview-row-${c.id}`}>
                        <td className="py-2 px-3 sticky left-0 bg-white z-10 min-w-[110px]">
                          {c.name}
                          <div className="text-[10px] text-[#8a8579]">{c.brand} · competencia{bus[c.id]?.bu ? ` · BU ${bus[c.id].bu}` : ''}</div>
                          {c.stale && (
                            <div className="text-[9.5px] text-[#991b1b] font-medium mt-0.5" data-testid={`stale-marker-${c.id}`}>
                              ⚠ Sin tracking reciente{c.last_captured_at ? ` · última captura ${fmtShort(c.last_captured_at)}` : ' · nunca capturado'}
                            </div>
                          )}
                          {c.prev_at && <div className="text-[9px] text-[#a8a397]" data-testid={`prev-at-${c.id}`}>vigente previo · {fmtShort(c.prev_at)}</div>}
                        </td>
                        {fuels.map(f => {
                          const p = c.prices?.[f] || {};
                          const ours = our[f] || {};
                          const prev = c.prev_prices?.[f] || {};
                          return (
                            <React.Fragment key={f}>
                              <td className="py-2 px-3 text-right border-l border-[#e8e5de]">
                                <PriceCell value={p.self} ourValue={ours.self} prev={prev.self}/>
                              </td>
                              {anyFull && (
                                <td className="py-2 px-3 text-right">
                                  <PriceCell value={p.full} ourValue={ours.full} prev={prev.full}/>
                                </td>
                              )}
                            </React.Fragment>
                          );
                        })}
                        <td className="py-2 px-2 text-right">
                          <div className="flex flex-col items-end gap-1">
                            {(() => {
                              if (c.official) {
                                return (
                                  <button
                                    type="button"
                                    onClick={() => setPhotoStation({ ...c, own: true, entry_id: null, svc: null })}
                                    className={photoBtnCls}
                                    data-testid={`photo-btn-monolith-${c.id}`}
                                    title="Estación propia · monolito de la última confirmación de cambio de precio"
                                  >
                                    <Camera size={11}/> Monolito
                                  </button>
                                );
                              }
                              const svcs = c.entry_ids
                                ? ['self', 'full'].filter(s => c.entry_ids[s])
                                : ['self', 'full'].filter(s => Object.values(c.prices || {}).some(p => (p?.[s] ?? 0) !== 0));
                              if (svcs.length === 0) {
                                return (
                                  <button type="button" onClick={() => setPhotoStation(c)} className={photoBtnCls} data-testid={`photo-btn-${c.id}`} title="Ver última foto aprobada">
                                    <Camera size={11}/> Ver
                                  </button>
                                );
                              }
                              return svcs.map(s => (
                                <button
                                  key={s}
                                  type="button"
                                  onClick={() => setPhotoStation({ ...c, entry_id: c.entry_ids?.[s] || c.entry_id, svc: s })}
                                  className={photoBtnCls}
                                  data-testid={`photo-btn-${c.id}-${s}`}
                                  title={s === 'full' ? 'Foto · Servicio completo' : 'Foto · Autoservicio'}
                                >
                                  <Camera size={11}/> {s === 'full' ? 'Full' : 'Auto'}
                                </button>
                              ));
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-[#8a8579] px-3 py-2 border-t border-[#e8e5de] bg-[#f8f6f1]">
                🟥 menor que nuestro precio · 🟩 mayor · neutro = igual o sin dato
              </p>
            </div>
          )}

          {mode === 'approving' && (
            <p className="text-[11.5px] text-[#3a3a3a] bg-[#dcfce7]/40 border border-[#bbf7d0] rounded-lg px-3 py-2">
              {!rulesReady ? (
                <span className="inline-flex items-center gap-2 text-[#92400e]"><Loader2 size={12} className="animate-spin"/> Cargando reglas de precios… los campos se habilitan al terminar.</span>
              ) : (
                <>
                  <strong>Reglas:</strong> Súper y Diesel = más bajo de competencia.
                  V-Power = Súper + Q{Number(params.diff_vp_super || 0).toFixed(2)} ·
                  Regular = Súper − Q{Number(params.diff_super_regular || 0).toFixed(2)} ·
                  ULSD = Diesel + Q{Number(params.diff_diesel_ulsd || 0).toFixed(2)}.
                  {' '}<strong>Dif. Auto/Full ({request.station_name}):</strong>{' '}
                  {fuels.filter(f => !ownAvail || ownAvail.includes(f)).map(f => `${FUEL_LABELS[f] || f} +${Number(pricingCfg?.product_adjustments?.[f]?.auto_full_diff ?? 1.1).toFixed(2)}`).join(' · ')}.
                </>
              )}
              {ENDING_BY_BRAND[(request.station_brand || '').trim().toLowerCase()] != null && (
                <> Terminación {(request.station_brand || '').trim().toLowerCase() === 'shell' ? '.x9' : '.x7'} para {request.station_brand} (se ajusta hacia abajo).</>
              )}
              {' '}Puedes ajustar manualmente antes de aprobar.
            </p>
          )}

          {/* OCR side-by-side (only when request is approved and station_push has OCR result) */}
          {stationPush && stationPush.ocr_match && Object.keys(stationPush.ocr_match).length > 0 && (
            <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden" data-testid="ocr-detail-section">
              <div className="px-4 py-2.5 bg-[#f8f6f1] border-b border-[#e8e5de] flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[11.5px] uppercase tracking-wider text-[#8a8579]">Lectura del monolito (OCR · Gemini)</p>
                {stationPush.photo_url && (
                  <a
                    href={photoURL(stationPush.photo_url)}
                    target="_blank" rel="noreferrer"
                    className="text-[11px] text-[#1c1c1c] underline underline-offset-2"
                    data-testid="ocr-view-photo"
                  >Ver foto</a>
                )}
              </div>
              <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {(() => {
                  // Build map: { fuel: { self: {expected, ocr, match}, full: {...} } }
                  const expected = stationPush.final_prices || {};
                  const ocr = stationPush.ocr_prices || {};
                  const match = stationPush.ocr_match || {};
                  const fuelKeys = ['vpower', 'super', 'regular', 'diesel', 'ulsd'].filter(f => expected[f] || ocr[f]);
                  return fuelKeys.map(fuel => (
                    <div key={fuel} className="border border-[#e8e5de] rounded-lg overflow-hidden">
                      <div className="px-3 py-1.5 bg-[#fafaf6] text-[11px] uppercase tracking-wider text-[#8a8579] border-b border-[#e8e5de]">
                        {fuel === 'vpower' ? 'V-Power' : fuel === 'super' ? 'Súper' : fuel === 'regular' ? 'Regular' : fuel === 'diesel' ? 'Diesel' : 'ULSD'}
                      </div>
                      <table className="w-full text-[12px]">
                        <thead className="bg-white">
                          <tr className="text-[10px] uppercase tracking-wider text-[#a8a397]">
                            <th className="text-left px-3 py-1.5 font-normal">Servicio</th>
                            <th className="text-right px-3 py-1.5 font-normal">Aprobado</th>
                            <th className="text-right px-3 py-1.5 font-normal">OCR detectado</th>
                            <th className="text-right px-3 py-1.5 font-normal w-[80px]">Estado</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#f0ede5]">
                          {['self', 'full'].map(svc => {
                            const exp = expected[fuel]?.[svc];
                            const det = ocr[fuel]?.[svc];
                            const m = match[`${fuel}.${svc}`];
                            if (exp == null && det == null) return null;
                            const stateCls = m === 'ok' ? 'bg-[#dcfce7] text-[#166534]'
                              : m === 'mismatch' ? 'bg-[#fee2e2] text-[#991b1b]'
                              : m === 'missing' ? 'bg-[#fef3c7] text-[#92400e]'
                              : 'bg-[#f3f0e9] text-[#5a5a5a]';
                            const stateLbl = m === 'ok' ? '✓ Coincide' : m === 'mismatch' ? '× Difiere' : m === 'missing' ? '? No leído' : '—';
                            return (
                              <tr key={svc} data-testid={`ocr-row-${fuel}-${svc}`}>
                                <td className="px-3 py-1.5 text-[#3a3a3a]">{svc === 'self' ? 'Autoservicio' : 'Servicio completo'}</td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums">{exp != null ? `Q${Number(exp).toFixed(2)}` : '—'}</td>
                                <td className={`px-3 py-1.5 text-right font-mono tabular-nums ${m === 'mismatch' ? 'text-[#991b1b] font-medium' : ''}`}>
                                  {det != null ? `Q${Number(det).toFixed(2)}` : '—'}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <span className={`text-[10.5px] px-2 py-0.5 rounded-full ${stateCls}`}>{stateLbl}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ));
                })()}
              </div>
              {stationPush.ocr_error && (
                <p className="px-4 pb-3 text-[11px] text-[#92400e]" data-testid="ocr-error">⚠ OCR no disponible: {stationPush.ocr_error}</p>
              )}
              <div className="px-4 pb-3 flex items-center gap-4 text-[10.5px] text-[#8a8579] flex-wrap">
                {stationPush.confirmed_at && <span>Foto subida: {new Date(stationPush.confirmed_at).toLocaleString('es-GT', {dateStyle:'short', timeStyle:'short'})}</span>}
                {stationPush.photo_age_min != null && <span>Antigüedad foto: <strong className="text-[#3a3a3a]">{stationPush.photo_age_min} min</strong></span>}
                {stationPush.gps_distance_m != null && <span>Distancia GPS: <strong className="text-[#3a3a3a]">{stationPush.gps_distance_m} m</strong></span>}
              </div>
            </div>
          )}

          {request.status === 'open' && (
            <div className="mt-1">
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Notas para la estación (opcional) · llegan en la notificación del cambio de precio"
                rows={2}
                className="w-full bg-white border border-[#e8e5de] focus:border-[#1c1c1c] focus:outline-none rounded-lg px-3 py-2 text-[12.5px] resize-none"
                data-testid="preview-notes-input"
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2 flex-row flex-wrap justify-end">
            {request.status !== 'open' ? (
              <>
                {reviewedFooter}
                <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 rounded-full text-[12px]">Cerrar</Button>
              </>
            ) : mode === 'view' ? (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 rounded-full text-[12px]" data-testid="preview-cancel">
                  <X size={13} className="mr-1.5"/> Cancelar
                </Button>
                <Button
                  variant="outline"
                  onClick={submitReject}
                  disabled={busy}
                  className="h-9 rounded-full text-[12px] border-[#991b1b] text-[#991b1b] hover:bg-[#fee2e2]"
                  data-testid="preview-reject"
                >
                  <XCircle size={13} className="mr-1.5"/> Rechazar
                </Button>
                <Button
                  onClick={startApprove}
                  disabled={busy || comps.length === 0}
                  className="h-9 rounded-full bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black text-[12px]"
                  data-testid="preview-start-approve"
                >
                  <CheckCircle2 size={13} className="mr-1.5"/> Aprobar
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setMode('view')} className="h-9 rounded-full text-[12px]" data-testid="preview-back">
                  ← Volver
                </Button>
                <Button
                  onClick={submitApprove}
                  disabled={busy}
                  className="h-9 rounded-full bg-[#15803d] text-white hover:bg-[#166534] text-[12px]"
                  data-testid="preview-confirm-approve"
                >
                  {busy ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <CheckCircle2 size={13} className="mr-1.5"/>}
                  Confirmar aprobación
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CompetitorPhotoDialog station={photoStation} open={!!photoStation} onOpenChange={(o) => !o && setPhotoStation(null)} />
    </>
  );
};

const TrackingRequestCard = ({ r, onReview, busyId, onPreview, selectable, selected, onToggleSelect }) => {
  const diffs = r.diffs || {};
  const diffEntries = Object.entries(diffs);
  const requestedAt = new Date(r.requested_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' });
  const compCount = (r.competitor_snapshot || []).length;
  return (
    <div className={`bg-white border rounded-xl p-4 ${selected ? 'border-[#1c1c1c] ring-1 ring-[#1c1c1c]' : 'border-[#e8e5de]'}`} data-testid={`tracking-request-${r.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5">
          {selectable && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelect(r.id)}
              className="mt-0.5 w-4 h-4 shrink-0 accent-[#1c1c1c] cursor-pointer"
              data-testid={`select-request-${r.id}`}
            />
          )}
          <div>
          <p className="text-[14px] font-medium">{r.station_name}</p>
          <p className="text-[11.5px] text-[#5a5a5a]">{r.requested_by_name} · {r.requested_by_role || 'usuario'} · {requestedAt}</p>
          {r.last_push && (
            <p className="text-[11px] text-[#1e40af] mt-0.5" data-testid={`card-last-push-${r.id}`}>
              Último envío: {fmtShort(r.last_push.effective_at)} · {ORIGIN_LABELS[r.last_push.origin] || r.last_push.origin}
            </p>
          )}
          {(r.pending_pushes || []).length > 0 && (
            <p className="text-[10.5px] text-[#92400e] mt-0.5" data-testid={`card-pending-pushes-${r.id}`}>
              {r.pending_pushes.length} push{r.pending_pushes.length > 1 ? 'es' : ''} pendiente{r.pending_pushes.length > 1 ? 's' : ''} de confirmar
            </p>
          )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="flex items-center gap-1.5">
            {r.station_operation && (
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full tracking-wider uppercase font-medium ${r.station_operation === 'RBA' ? 'bg-[#dcfce7] text-[#166534]' : 'bg-[#fef3c7] text-[#92400e]'}`}
                data-testid={`card-operation-${r.id}`}
              >
                {r.station_operation}
              </span>
            )}
            {r.source === 'auto_tracking' && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#e0e7ff] text-[#0f1d6f] tracking-wider uppercase" data-testid={`card-auto-badge-${r.id}`}>Auto · tracking</span>
            )}
          </div>
          {r.rsop_compare && (
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.rsop_compare.status === 'match' ? 'bg-[#dcfce7] text-[#166534]' : 'bg-[#fef3c7] text-[#92400e]'}`}
              data-testid={`card-rsop-compare-${r.id}`}
              title={r.rsop_compare.status === 'match'
                ? 'El precio vigente de la estación está en su RSOP (sugerido con flete/ajustes) — sin margen para subir.'
                : `Vigente vs RSOP: ${Object.entries(r.rsop_compare.diffs || {}).map(([f, d]) => `${FUEL_LABELS[f] || f} ${d > 0 ? '+' : ''}${d.toFixed(2)}`).join(' · ')}`}
            >
              {r.rsop_compare.status === 'match' ? 'En sugerido' : 'Distinto al sugerido'}
            </span>
          )}
          {r.station_tm && (
            <span className="text-[10px] text-[#8a8579]" data-testid={`card-tm-${r.id}`}>TM: {r.station_tm}</span>
          )}
          {r.station_trade_area && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f3e8ff] text-[#6b21a8] uppercase tracking-wider" data-testid={`card-trade-area-${r.id}`}>
              TA · {r.station_trade_area}
            </span>
          )}
          {r.incomplete && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]"
              data-testid={`card-incomplete-badge-${r.id}`}
              title={(r.missing_markers || []).length ? `Sin tracking reciente de: ${r.missing_markers.join(', ')}` : 'Tracking incompleto'}
            >
              <AlertTriangle size={11}/> Incompleto{(r.missing_markers || []).length ? ` · faltó ${r.missing_markers.length} marker(s)` : ' · 1h'}
            </span>
          )}
          {r.forced_within_window && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]"
              title={`El último cambio oficial de precio de esta estación fue hace menos de ${r.window_hours}h. El usuario confirmó el envío igualmente cuando aún faltaban ${r.hours_remaining_at_request}h para completar la ventana de ${r.window_hours}h.`}
            >
              <AlertTriangle size={11}/> Enviado antes de {r.window_hours}h · faltaban {r.hours_remaining_at_request}h
            </span>
          )}
          {r.recent_push && r.status === 'open' && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#991b1b]"
              data-testid={`card-recent-push-badge-${r.id}`}
              title={`Ya se envió un push a esta estación ${r.recent_push.confirmed_at ? '(confirmado con foto)' : '(pendiente de confirmar)'} · evita tomar doble acción`}
            >
              <AlertTriangle size={11}/> Push enviado hace {Math.max(1, Math.round((Date.now() - new Date(r.recent_push.sent_at).getTime()) / 3600000))} h
            </span>
          )}
          {r.resolved_by_ta && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#ede9fe] text-[#5b21b6]" data-testid={`card-ta-resolved-badge-${r.id}`}>
              Resuelto por Trade Área
            </span>
          )}
          {(r.resend_count || 0) > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full bg-[#e0e7ff] text-[#0f1d6f]"
              data-testid={`card-resend-badge-${r.id}`}
              title="Se envió este tracking varias veces · se consolidó en una sola tarjeta con los datos más recientes"
            >
              Reenviado ×{r.resend_count}{r.last_resent_at ? ` · ${fmtShort(r.last_resent_at)}` : ''}
            </span>
          )}
          <span className="text-[10px] tracking-wide text-[#8a8579] uppercase">Ventana {r.window_hours}h</span>
        </div>
      </div>

      {diffEntries.length > 0 ? (
        <div className="bg-[#f8f6f1] border border-[#e8e5de] rounded-lg p-3 mb-3">
          <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-2">Diferencias detectadas</p>
          <div className="space-y-1">
            {diffEntries.map(([key, d]) => {
              const [fuel, svc] = key.split('.');
              const sign = d.delta < 0 ? 'lower' : 'higher';
              return (
                <div key={key} className="flex items-center justify-between gap-2 text-[12px]" data-testid={`diff-${r.id}-${key}`}>
                  <span className="text-[#3a3a3a]">{FUEL_LABELS[fuel] || fuel} · {SVC_LABEL[svc] || svc}</span>
                  <span className="font-mono tabular-nums text-[#5a5a5a]">Q{Number(d.our).toFixed(2)} vs Q{Number(d.competitor).toFixed(2)}</span>
                  <span className={`inline-flex items-center gap-0.5 font-mono tabular-nums text-[12px] px-2 py-0.5 rounded ${sign === 'lower' ? 'bg-[#fee2e2] text-[#991b1b]' : 'bg-[#dcfce7] text-[#166534]'}`}>
                    {sign === 'lower' ? <ArrowDown size={11}/> : <ArrowUp size={11}/>}
                    Q{Math.abs(Number(d.delta)).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : freshDiffs(r).length > 0 ? (
        <div className="bg-[#f8f6f1] border border-[#e8e5de] rounded-lg p-3 mb-3" data-testid={`card-consolidated-${r.id}`}>
          <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-2">Diferencias vs markers <span className="normal-case text-[#0f1d6f]">· comparación en Autoservicio</span></p>
          <div className="flex flex-wrap gap-1.5">
            {freshDiffs(r).slice(0, 8).map((d, i) => (
              <span key={i} className={`inline-flex items-center gap-1 text-[11px] font-mono tabular-nums px-2 py-0.5 rounded ${d.diff < 0 ? 'bg-[#fee2e2] text-[#991b1b]' : 'bg-[#dcfce7] text-[#166534]'}`}>
                {d.marker_name} · {FUEL_LABELS[d.fuel] || d.fuel} {d.diff > 0 ? '+' : ''}{Number(d.diff).toFixed(2)}
              </span>
            ))}
            {freshDiffs(r).length > 8 && (
              <span className="text-[10.5px] text-[#8a8579]">+{freshDiffs(r).length - 8} más</span>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[11.5px] text-[#8a8579] mb-3">Sin diferencias específicas en el snapshot.</p>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => onPreview(r)}
          className="flex-1 h-10 rounded-full text-[12.5px] bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black"
          data-testid={`preview-${r.id}`}
        >
          <Eye size={13} className="mr-1.5"/>
          {r.status === 'open'
            ? `Revisar${compCount > 0 ? ` · ${compCount} competidor${compCount > 1 ? 'es' : ''}` : ''}`
            : `Ver detalle${compCount > 0 ? ` · ${compCount} competidor${compCount > 1 ? 'es' : ''}` : ''}`}
        </Button>
        {r.status === 'open' && (
          <Button
            type="button"
            disabled={busyId === r.id}
            onClick={() => {
              if (window.confirm(`¿Marcar como REVISADO el tracking de ${r.station_name}?\n\nSe archiva sin modificar precios ni rechazar (quedará en la pestaña Revisadas).`)) {
                onReview(r.id, 'review');
              }
            }}
            className="h-10 rounded-full text-[12.5px] px-4 bg-white border border-[#e8e5de] text-[#15803d] hover:bg-[#dcfce7]"
            title="Archivar sin cambio de precios"
            data-testid={`mark-reviewed-${r.id}`}
          >
            <CheckCheck size={14} className="mr-1.5"/>Revisado
          </Button>
        )}
      </div>

      {r.status !== 'open' && (
        <p className="text-[11px] text-[#8a8579] mt-2.5">
          {r.status === 'approved' ? 'Aprobada' : r.status === 'reviewed' ? 'Revisada · sin cambio' : 'Rechazada'} por {r.reviewed_by_name || '—'}
          {r.reviewed_at ? ` · ${new Date(r.reviewed_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
        </p>
      )}
    </div>
  );
};

const AdminTrackingRequests = ({ embedded = false }) => {
  const [status, setStatus] = useState('open');
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [previewReq, setPreviewReq] = useState(null);
  const [reviewAllOpen, setReviewAllOpen] = useState(false);
  const [busyAll, setBusyAll] = useState(false);
  const [selIds, setSelIds] = useState([]);
  const [opF, setOpF] = useState('');
  const [tmF, setTmF] = useState('');
  const [sugF, setSugF] = useState('');
  useEffect(() => { setSelIds([]); }, [status]);
  const toggleSel = (id) => setSelIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const reviewSelected = async () => {
    if (!selIds.length) return;
    setBusyAll(true);
    try {
      const r = await api.post('/price-change-requests/review-bulk', { ids: selIds });
      toast.success(`${r.data.updated} solicitud(es) marcadas como revisadas · sin cambio`);
      setSelIds([]);
      load({ silent: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al procesar');
    } finally { setBusyAll(false); }
  };
  const { restoring, clearRestoring, minimizeWindow } = useWindows();

  useEffect(() => {
    if (restoring?.type === 'tracking-preview' && restoring.data) {
      setPreviewReq(restoring.data);
      clearRestoring();
    }
  }, [restoring, clearRestoring]);

  const minimizePreview = (req) => {
    if (!req) return;
    minimizeWindow({
      key: req.id, type: 'tracking-preview',
      title: req.station_name, subtitle: 'Tracking',
      route: '/admin/tracking-requests', data: req,
    });
    setPreviewReq(null);
  };

  const tmOptions = [...new Set(items.flatMap(r => (r.station_tm || '').split(' / ').filter(Boolean)))].sort();

  const filteredItems = items.filter(r => {
    if (opF && r.station_operation !== opF) return false;
    if (tmF && !(r.station_tm || '').split(' / ').includes(tmF)) return false;
    if (sugF && (r.rsop_compare?.status || '') !== sugF) return false;
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    const markers = (r.consolidated_diffs || []).map(d => d.marker_name).join(' ');
    return [r.station_name, r.station_bu, r.requested_by_name, markers]
      .some(x => (x || '').toLowerCase().includes(t));
  });

  const [openMismatch, setOpenMismatch] = useState(0);

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const r = await api.get('/price-change-requests', { params: { status_f: status } });
      setItems(r.data || []);
      const openRows = status === 'open'
        ? (r.data || [])
        : ((await api.get('/price-change-requests', { params: { status_f: 'open' } })).data || []);
      setOpenMismatch(openRows.filter(x => x.rsop_compare?.status === 'mismatch').length);
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const review = async (id, action, extra = {}) => {
    setBusyId(id);
    try {
      const resp = await api.patch(`/price-change-requests/${id}`, { action, ...extra });
      const ta = resp.data?.ta_propagated;
      if (action === 'approve' && Array.isArray(ta) && ta.length > 0) {
        const names = ta.map(x => x.station_name).join(', ');
        toast.success(`Aprobada · push enviado también a ${ta.length} estación(es) del Trade Área${resp.data?.ta_name ? ` ${resp.data.ta_name}` : ''}: ${names}`, { duration: 10000 });
      } else {
        toast.success(action === 'approve' ? 'Solicitud aprobada' : action === 'review' ? 'Marcada como revisada · sin cambio de precios' : 'Solicitud rechazada');
      }
      // Refresco silencioso: la lista no se desmonta y el scroll se queda donde estaba
      load({ silent: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al procesar');
    } finally {
      setBusyId(null);
    }
  };

  const reviewAll = async () => {
    setBusyAll(true);
    try {
      const r = await api.post('/price-change-requests/review-all');
      toast.success(`${r.data.updated} solicitud(es) archivadas como revisadas`);
      setReviewAllOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al procesar');
    } finally { setBusyAll(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-tracking-requests-page">
      {!embedded && (
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Tracking de precios</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">Solicitudes de cambio enviadas por los capturadores cuando detectan diferencias con la competencia.</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex gap-1 bg-white border border-[#e8e5de] rounded-full p-1">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-1.5 rounded-full text-[12px] transition ${status === t.key ? 'bg-[#1c1c1c] text-[#f8f6f1]' : 'text-[#5a5a5a] hover:bg-[#f3f0e9]'}`}
            >
              {t.label}
              {t.key === 'open' && openMismatch > 0 && (
                <span
                  className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#fef3c7] text-[#92400e]"
                  data-testid="open-mismatch-count"
                  title={`${openMismatch} tracking(s) pendiente(s) con precio distinto al sugerido`}
                >
                  {openMismatch} ≠ sug.
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar estación, BU, marker…" className="h-9 pl-8 bg-white text-[12.5px] rounded-full" data-testid="tracking-search"/>
        </div>
        {q && <span className="text-[11px] text-[#8a8579]" data-testid="tracking-search-count">{filteredItems.length} resultado(s)</span>}
        <select
          value={opF}
          onChange={e => setOpF(e.target.value)}
          className="h-9 px-3 rounded-full border border-[#e8e5de] bg-white text-[12px] text-[#3a3a3a] focus:outline-none"
          data-testid="filter-operation"
        >
          <option value="">Operación: todas</option>
          <option value="RBA">RBA</option>
          <option value="DO">DO</option>
        </select>
        <select
          value={tmF}
          onChange={e => setTmF(e.target.value)}
          className="h-9 px-3 rounded-full border border-[#e8e5de] bg-white text-[12px] text-[#3a3a3a] focus:outline-none max-w-[200px]"
          data-testid="filter-tm"
        >
          <option value="">TM: todos</option>
          {tmOptions.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={sugF}
          onChange={e => setSugF(e.target.value)}
          className="h-9 px-3 rounded-full border border-[#e8e5de] bg-white text-[12px] text-[#3a3a3a] focus:outline-none"
          data-testid="filter-rsop"
        >
          <option value="">Sugerido: todos</option>
          <option value="mismatch">Distinto al sugerido</option>
          <option value="match">En sugerido</option>
        </select>
        {status === 'open' && selIds.length > 0 && (
          <Button
            type="button"
            onClick={reviewSelected}
            disabled={busyAll}
            className="h-9 rounded-full text-[12px] ml-auto bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black"
            data-testid="review-selected-btn"
          >
            {busyAll ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <CheckCheck size={13} className="mr-1.5"/>}
            Marcar {selIds.length} como revisado{selIds.length > 1 ? 's' : ''}
          </Button>
        )}
        {status === 'open' && items.length > 0 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setReviewAllOpen(true)}
            className={`h-9 rounded-full text-[12px] ${selIds.length > 0 ? '' : 'ml-auto'}`}
            data-testid="review-all-btn"
          >
            <CheckCheck size={13} className="mr-1.5"/> Marcar todas como revisadas
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-10 text-center">
          <Inbox size={28} strokeWidth={1.4} className="mx-auto text-[#a8a397] mb-2"/>
          <p className="text-[13px] text-[#8a8579]">{q ? `Sin resultados para "${q}" en este estado.` : 'Sin solicitudes en este estado.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredItems.map(r => (
            <TrackingRequestCard
              key={r.id} r={r} onReview={review} busyId={busyId} onPreview={setPreviewReq}
              selectable={status === 'open'} selected={selIds.includes(r.id)} onToggleSelect={toggleSel}
            />
          ))}
        </div>
      )}

      <PricePreviewModal
        open={!!previewReq}
        onOpenChange={(o) => !o && setPreviewReq(null)}
        request={previewReq}
        onReview={review}
        busy={!!busyId}
        onMinimize={minimizePreview}
        onCorrected={() => load({ silent: true })}
      />

      <Dialog open={reviewAllOpen} onOpenChange={(o) => { if (!busyAll) setReviewAllOpen(o); }}>
        <DialogContent className="max-w-md bg-[#f8f6f1]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">Marcar todas como revisadas</DialogTitle>
          </DialogHeader>
          <p className="text-[12.5px] text-[#3a3a3a] leading-relaxed">
            Se archivarán <strong>{items.length}</strong> solicitud(es) pendiente(s) como
            <strong> Revisadas · sin cambio de precios</strong>, con fecha de hoy.
            Dejarán de aparecer en Pendientes y podrás consultarlas en la pestaña "Revisadas".
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReviewAllOpen(false)} disabled={busyAll} data-testid="review-all-cancel">Cancelar</Button>
            <Button type="button" onClick={reviewAll} disabled={busyAll} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="review-all-confirm">
              {busyAll ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <CheckCheck size={13} className="mr-1.5"/>}
              Revisar todas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrackingRequests;
export { PricePreviewModal };
